import React, { useEffect, useState } from 'react';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@tanium/react-theme-context';
import { Box } from '@tanium/react-box';
import { K8sRoleTargetAccess } from 'gql/K8sRoleTargetAccess';
import { getSessionConfig } from 'utils/Config';
import IDropdownOption from 'common/IDropdownOption';
import APIAccessType from 'common/IAPIAccessType';
import KubernetesClusterRoleAccess from './KubernetesClusterRoleAccess';
import KubernetesRoleAccess from './KubernetesRoleAccess';

export const API_ACCESS_TYPE = 'apiAccessType';

interface KubernetesClusterAccessProps {
  k8sTargetAccessList?: K8sRoleTargetAccess[];
}
export const KubernetesClusterAPIAccess = ({
  k8sTargetAccessList,
}: KubernetesClusterAccessProps): React.ReactElement => {
  const config = getSessionConfig();
  const theme = useTheme();
  const isGovCloud = config?.isGovCloud;
  const { t } = useTranslation();
  const formInputIdPrefix = 'create-request-';
  const [selectedAPIAccessType, setSelectedAPIAccessType] = useState<APIAccessType | undefined>();
  const [apiAccessTypeOptions, setAPIAccessTypeOptions] = useState<IDropdownOption[] | undefined>();

  useEffect(() => {
    const buildOption = (accessType: APIAccessType) => {
      return {
        label: t(`API_TYPE_${accessType}`),
        value: accessType,
        description: t(`API_TYPE_${accessType}_INFO`),
      };
    };
    const options = [];
    if (k8sTargetAccessList?.find((ta) => ta.apiAccessType === APIAccessType.ROLE)) {
      options.push(buildOption(APIAccessType.ROLE));
    }
    if (k8sTargetAccessList?.find((ta) => ta.apiAccessType === APIAccessType.CLUSTER_ROLE)) {
      options.push(buildOption(APIAccessType.CLUSTER_ROLE));
    }
    setAPIAccessTypeOptions(options);
  }, [t, k8sTargetAccessList]);

  return (
    <Box>
      <Box display="inline-flex" mr={1}>
        <Field name={API_ACCESS_TYPE}>
          {({ field, form }: FieldProps): React.ReactElement => (
            <FormRow
              label={t('API_ACCESS_TYPE')}
              required
              description={
                <Box>
                  <Box>{t('API_ACCESS_TYPE_DESCRIPTION')}</Box>
                  {isGovCloud && <Box color={theme.colors.warning}>{t('ROLE_BASTION_WARNING')}</Box>}
                </Box>
              }
              errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
              labelId={`${formInputIdPrefix}${field.name}`}
              data-testid={`${formInputIdPrefix}${field.name}`}
            >
              <FormSelectInput
                onChange={(option): void => {
                  void form.setFieldValue(field.name, option.value);
                  setSelectedAPIAccessType(option.value);
                }}
                value={apiAccessTypeOptions?.find((option) => option.value === field.value)}
                inputId={`${formInputIdPrefix}${field.name}`}
                options={apiAccessTypeOptions}
                aria-label={t('API_ACCESS_TYPE')}
              />
            </FormRow>
          )}
        </Field>
      </Box>
      {selectedAPIAccessType === APIAccessType.CLUSTER_ROLE && (
        <KubernetesClusterRoleAccess k8sTargetAccessList={k8sTargetAccessList} />
      )}
      {selectedAPIAccessType === APIAccessType.ROLE && (
        <KubernetesRoleAccess k8sTargetAccessList={k8sTargetAccessList} />
      )}
    </Box>
  );
};

export default KubernetesClusterAPIAccess;
